import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Music, Filter, Heart, Play, ChevronDown, Mic } from 'lucide-react';
import { useData } from '../context/DataContext';
import type { Song } from '../services/SongService';

export const SongCatalog: React.FC = () => {
  const { songService } = useData();
  const navigate = useNavigate();
  
  const [songs, setSongs] = useState<Song[]>([]);
  const [loading, setLoading] = useState(true);
  const [favorites] = useState(new Set<string>());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState<string>('All');
  const [selectedLanguage, setSelectedLanguage] = useState<string>('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('All');
  const [sortBy, setSortBy] = useState<string>('popularity');
  
  useEffect(() => {
    const loadSongs = async () => {
      try {
        console.log('SongCatalog: Loading songs...');
        const songsData = await songService.getAllSongs();
        console.log('SongCatalog: Received songs:', songsData);
        console.log('SongCatalog: First song cover URL:', songsData[0]?.cover_url);
        setSongs(songsData);
      } catch (error) {
        console.error('Error loading songs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSongs();
  }, []);

  const genres = ['All', 'Pop', 'Country', 'Indie', 'Open Mic', 'Rock', 'R&B', 'Hip Hop', 'Electronic', 'Jazz'];
  const languages = ['All', 'English', 'Spanish', 'French', 'Korean', 'Japanese', 'Italian', 'German'];
  const difficulties = ['All', 'Easy', 'Medium', 'Hard', 'Expert'];
  
  const filteredSongs = songs.filter(song => {
    const matchesSearch = song.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         song.artist.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGenre = selectedGenre === 'All' || song.genre === selectedGenre;
    const matchesLanguage = selectedLanguage === 'All' || song.language === selectedLanguage;
    const matchesDifficulty = selectedDifficulty === 'All' || song.difficulty === selectedDifficulty;
    
    return matchesSearch && matchesGenre && matchesLanguage && matchesDifficulty;
  });
  
  // Sort songs
  const sortedSongs = [...filteredSongs].sort((a, b) => {
    switch (sortBy) {
      case 'popularity':
        return b.popularity - a.popularity;
      case 'title':
        return a.title.localeCompare(b.title);
      case 'artist':
        return a.artist.localeCompare(b.artist);
      case 'newest':
        return b.releaseYear - a.releaseYear;
      default:
        return 0;
    }
  });
  
  const playSong = (song: Song) => {
    navigate(`/kod-studio/${song.id}`);
  };
  
  const recordSong = (e: React.MouseEvent, song: Song) => {
    e.stopPropagation(); // Prevent triggering the row click
    console.log('SongCatalog: Recording song:', song);
    console.log('SongCatalog: Cover art URL:', song.cover_url);
    navigate(`/record/${song.id}`);
  };
  
  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="container mx-auto p-6">
      <h1 className="section-title">Audition Dashboard</h1>
      
      {/* Search and Filters */}
      <div className="card mb-6">
        <h2 className="subtitle mb-4">Select a Song to Perform</h2>
        
        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/50" size={18} />
          <input
            type="text"
            placeholder="Search songs, artists..."
            className="w-full pl-10 pr-4 py-2 bg-kod-purple-700/70 text-white border border-kod-purple-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-kod-accent"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        {/* Filter Controls */}
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-white/80 mb-1">Genre</label>
            <div className="relative">
              <select
                className="block w-full pl-3 pr-10 py-2 text-white bg-kod-purple-700/70 border border-kod-purple-600 rounded-md focus:outline-none focus:ring-2 focus:ring-kod-accent appearance-none"
                value={selectedGenre}
                onChange={(e) => setSelectedGenre(e.target.value)}
              >
                {genres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50" />
            </div>
          </div>
          
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-white/80 mb-1">Language</label>
            <div className="relative">
              <select
                className="block w-full pl-3 pr-10 py-2 text-white bg-kod-purple-700/70 border border-kod-purple-600 rounded-md focus:outline-none focus:ring-2 focus:ring-kod-accent appearance-none"
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
              >
                {languages.map(language => (
                  <option key={language} value={language}>{language}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50" />
            </div>
          </div>
          
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-white/80 mb-1">Difficulty</label>
            <div className="relative">
              <select
                className="block w-full pl-3 pr-10 py-2 text-white bg-kod-purple-700/70 border border-kod-purple-600 rounded-md focus:outline-none focus:ring-2 focus:ring-kod-accent appearance-none"
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
              >
                {difficulties.map(difficulty => (
                  <option key={difficulty} value={difficulty}>{difficulty}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50" />
            </div>
          </div>
          
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-white/80 mb-1">Sort By</label>
            <div className="relative">
              <select
                className="block w-full pl-3 pr-10 py-2 text-white bg-kod-purple-700/70 border border-kod-purple-600 rounded-md focus:outline-none focus:ring-2 focus:ring-kod-accent appearance-none"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="popularity">Popularity</option>
                <option value="title">Title A-Z</option>
                <option value="artist">Artist A-Z</option>
                <option value="newest">Newest</option>
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Song List */}
      {loading ? (
        <div className="card flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-kod-accent"></div>
        </div>
      ) : (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="subtitle mb-0">All Songs</h2>
            <div className="text-sm text-white/60">Showing {sortedSongs.length} results</div>
          </div>
          
          {/* Songs Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-kod-purple-600">
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider"></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Song</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Artist</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Genre</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/50 uppercase tracking-wider">Difficulty</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-white/50 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-kod-purple-600/50">
                {sortedSongs.map(song => (
                  <tr key={song.id} className="hover:bg-kod-purple-700/50 cursor-pointer transition-colors" onClick={() => playSong(song)}>
                    <td className="px-4 py-4">
                      <img src={song.cover_url} alt={song.title} className="w-10 h-10 rounded-md object-cover" />
                    </td>
                    <td className="px-4 py-4 font-medium text-white">{song.title}</td>
                    <td className="px-4 py-4 text-white/70">{song.artist}</td>
                    <td className="px-4 py-4 text-white/70">{song.genre}</td>
                    <td className="px-4 py-4 text-white/70">{formatDuration(song.duration)}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        song.difficulty === 'Easy' ? 'bg-green-900/30 text-green-300' :
                        song.difficulty === 'Medium' ? 'bg-blue-900/30 text-blue-300' :
                        song.difficulty === 'Hard' ? 'bg-orange-900/30 text-orange-300' :
                        'bg-red-900/30 text-red-300'
                      }`}>
                        {song.difficulty}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(song.id);
                          }}
                          className="p-1 text-white/50 hover:text-kod-accent transition-colors"
                          title="Add to favorites"
                        >
                          <Heart size={18} fill={favorites.has(song.id) ? 'currentColor' : 'none'} className={favorites.has(song.id) ? 'text-kod-accent' : ''} />
                        </button>
                        <button 
                          onClick={(e) => recordSong(e, song)}
                          className="px-3 py-1.5 bg-kod-accent hover:bg-kod-accent-dark text-white rounded-md text-xs font-medium transition-colors"
                        >
                          Sing <Play size={14} className="inline ml-1" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {sortedSongs.length === 0 && (
            <div className="text-center py-8">
              <Music size={48} className="mx-auto text-white/30 mb-3" />
              <p className="text-white/50">No songs found matching your criteria</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};